import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Typography, TextField, Button, Stack, Alert, Snackbar, Divider, MenuItem, Checkbox, FormControlLabel } from '@mui/material';
import { useAuth } from '../context/AuthContext';

const SeriesMaster = ({ id, mode, onPopupCancel, connectionString, companyId: propsCompanyId }) => {

    const { user, selectedCompany } = useAuth();
    const { id: routeId } = useParams();

    const recordId = mode === 'edit' ? id : routeId;

    const navigate = useNavigate();

    const [formError, setFormError] = useState('');

    const [bookName, setBookName] = useState('');
    const [seriesName, setSeriesName] = useState('');
    const [prefixCode, setPrefixCode] = useState('');
    const [suffixCode, setSuffixCode] = useState('');
    const [isDefault, setIsDefault] = useState(false);

    // ✅ STATIC BOOK LIST (replace or keep empty)
    const bookList = [
        { BookId: 1, BookName: 'Sale Challan - Sale Invoice' },
        { BookId: 2, BookName: 'Proforma Invoice' },
        { BookId: 3, BookName: 'Credit Note' }
    ];

    const path = `${connectionString}/api`;

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const userId = user?.UserId || null;
    const companyId = (mode === 'comp' || mode === 'edit') ? propsCompanyId : selectedCompany?.CompId;
    const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red', }, };

    const AppRegID = selectedCompany?.AppRegID || null;

    // 🔁 Fetch Edit Data (UNCHANGED)
    useEffect(() => {
        if (mode === 'edit' && recordId) {
            const fetchData = async () => {
                try {
                    const response = await fetch(`${path}/series/getDetails/${recordId}`);
                    const data = await response.json();

                    setBookName(data.BookName);
                    setSeriesName(data.SeriesName);
                    setPrefixCode(data.PrefixCode);
                    setSuffixCode(data.SuffixCode);
                    setIsDefault(data.IsDefault === 1);

                } catch (error) {
                    console.error('Error fetching Series data:', error);
                }
            };
            fetchData();
        }
    }, [recordId, mode, path]);

    const funSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        try {
            if (!bookName) {
                setFormError('Book Name is required');
                return;
            }

            if (!seriesName.trim()) {
                setFormError('Series Name is required');
                return;
            }

            if (!prefixCode.trim()) {
                setFormError('Prefix Code is required');
                return;
            }

            if (mode === 'add' || mode === 'comp') {
                await axios.post(`${path}/series/Insert`, { bookName, seriesName, prefixCode, suffixCode, companyId, AppRegID, userId, IsDefault: isDefault ? 1 : 0 });

                setBookName('');
                setSeriesName('');
                setPrefixCode('');
                setSuffixCode('');
                setSnackbar({ open: true, message: 'Data inserted successfully', severity: 'success' });
                if (onPopupCancel) {
                    setTimeout(() => { onPopupCancel() }, 600);
                }

            } else if (mode === 'edit') {

                await axios.put(`${path}/series/Update/${recordId}`, { bookName, seriesName, prefixCode, suffixCode, companyId, AppRegID, editUserId: userId, IsDefault: isDefault ? 1 : 0 });
                setSnackbar({ open: true, message: 'Data updated successfully', severity: 'success' });
                if (onPopupCancel) {
                    setTimeout(() => { onPopupCancel() }, 600);
                }
            }

        } catch (error) {
            console.error('Error inserting/updating data:', error);
            const errorMessage = error.response?.data?.message || `Error ${mode === 'add' || mode === 'comp' ? 'inserting' : 'updating'} data`;
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        }
    };

    const funReset = () => {
        if (onPopupCancel) {
            onPopupCancel();
        } else {
            setFormError('');
            setBookName('');
            setSeriesName('');
            setPrefixCode('');
            setSuffixCode('');
            navigate('/main/seriesDetail');
        }
    };

    // ──────────────── Alt+S (Save/Update) & Alt+C (Cancel) shortcuts ────────────────
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                funSubmit(e);
            } else if (e.code === "KeyC") {
                e.preventDefault();
                funReset();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)' }} />
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}> {mode === 'add' || mode === 'comp' ? 'Add Series' : 'Edit Series'} </Typography>
            </Box>
            <Divider />
            <Box component="form" onSubmit={funSubmit} noValidate>
                <Box sx={{ px: 2.5, pt: 2, pb: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Book Dropdown (STATIC) */}
                    <TextField
                        select
                        fullWidth
                        label="Book Name"
                        value={bookName}
                        size="small"
                        onChange={(e) => setBookName(e.target.value)}
                        required
                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                    >
                        {bookList.map((item) => (
                            <MenuItem key={item.BookId} value={item.BookName}>
                                {item.BookName}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        label="Series Name"
                        value={seriesName}
                        size="small"
                        onChange={(e) => setSeriesName(e.target.value)}
                        required
                        InputLabelProps={{ sx: requiredAsteriskStyle }}
                    />

                    <TextField
                        fullWidth
                        label="Prefix Code"
                        value={prefixCode}
                        size="small"
                        onChange={(e) => setPrefixCode(e.target.value.replace(/\//g, ''))}
                    />

                    <TextField
                        fullWidth
                        label="Suffix Code"
                        value={suffixCode}
                        size="small"
                        onChange={(e) => setSuffixCode(e.target.value)}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                            />
                        }
                        label="Set as Default"
                    />

                    {formError && <Alert severity="error">{formError}</Alert>}
                </Box>
                <Divider sx={{ mt: 1.5 }} />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button type="button" variant="outlined" color="inherit" size="small" onClick={funReset}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" size="small">
                        {mode === 'add' || mode === 'comp' ? 'Save' : 'Update'}
                    </Button>
                </Box>
            </Box>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SeriesMaster;